import {defaultState} from '../reducers/state';
import {
    ApplicantInfoSummaryDTO,
    CalculatorApplicantSummaryDTO,
    ContinueHeaderSummaryDTO,
    DesiredVehicleApplicantSummaryDTO,
    DocumentsSummaryDTO,
    EmploymentInfoSummaryDTO,
    FieldApplicantSummary,
    HomeApplicantSummaryDTO,
    LoanApplicationApplicantSummaryDTO,
    LoanApplicationTurboCashSummaryDTO,
    PersonalApplicantSummaryDTO,
    ReferencesSummaryDTO,
    WorkApplicantSummaryDTO
} from '../types/mx/creditoReal/epics';
import {
    CalculatorStepViewFieldErrorState,
    HomeDataStepViewFieldErrorState,
    PersonalDataStepViewFieldErrorState,
    WorkDataStepViewErrorFieldState
} from '../types/mx/creditoReal/reducers';
import * as GlobalConstants from '../constants/GlobalConstants';
import * as ValidateConstant from '../constants/ValidateConstant';
import {
    PersonalDataTurboCashStepViewFieldErrorState,
    WorkDataTurboCashStepViewErrorFieldState
} from '../types/mx/turboCash/reducers';
import {isEmpty} from '../../common/utils/ValidationUtils';
import {CR_DEPENDENT_CATALOG_ID} from '../constants/MotorcreditConstants';

export const getPersonalDataFieldSummary =
    (personalDataResponse: LoanApplicationApplicantSummaryDTO)
        : FieldApplicantSummary<PersonalApplicantSummaryDTO, PersonalDataStepViewFieldErrorState> => {
        const isUndefined = personalDataResponse.personal === undefined;
        const data = !isUndefined ? personalDataResponse.personal : new PersonalApplicantSummaryDTO();
        const fieldErrors =
            isUndefined ?
                defaultState.creditoReal.ui.personalDataStepView.fieldErrors :
                new PersonalDataStepViewFieldErrorState();
        fieldErrors.nationalityFieldError = !data.nationality?.id ? ValidateConstant.ERROR_REQUIRED : null;
        return new FieldApplicantSummary(data, fieldErrors);
    };

export const getContinueHeaderFieldSummary =
    (continueHeaderResponse: LoanApplicationTurboCashSummaryDTO)
        : ContinueHeaderSummaryDTO => {
        const isUndefined = continueHeaderResponse.continueHeader === undefined;
        const data = !isUndefined ? continueHeaderResponse.continueHeader : new ContinueHeaderSummaryDTO();
        return new ContinueHeaderSummaryDTO(
            data.completeName,
            data.firstName,
            data.lastName,
            data.secondName,
            data.secondLastName,
            data.vehiclePrice,
            data.financingDownPayment,
            data.financingTerm,
            data.monthlyPayment,
            data.financingAmount
        );
    };

export const getPersonalApplicantFieldSummary =
    (personalApplicantResponse: LoanApplicationTurboCashSummaryDTO)
        : FieldApplicantSummary<ApplicantInfoSummaryDTO, PersonalDataTurboCashStepViewFieldErrorState> => {
        const isUndefined = personalApplicantResponse.applicantInfo === undefined;
        const data = !isUndefined ? personalApplicantResponse.applicantInfo : new ApplicantInfoSummaryDTO();
        const fieldErrors =
            isUndefined ?
                defaultState.turboCash.ui.personalDataStepView.fieldErrors :
                new PersonalDataTurboCashStepViewFieldErrorState(
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                    null,
                    !!data.civilStatus && data.civilStatus.value ? null : ValidateConstant.ERROR_REQUIRED,
                    !!data.regimen && data.regimen.value ? null : ValidateConstant.ERROR_REQUIRED,
                    !!data.roleAtHome && data.roleAtHome.value ? null : ValidateConstant.ERROR_REQUIRED,
                    !!data.studiesLevel && data.studiesLevel.value ? null : ValidateConstant.ERROR_REQUIRED,
                    !!data.houseType && data.houseType.value ? null : ValidateConstant.ERROR_REQUIRED,
                    data.economicDependents ? null : ValidateConstant.ERROR_REQUIRED,
                    data.curp ? null : ValidateConstant.ERROR_REQUIRED,
                    parseInt(data.holdTime, 10) > 0 ? null : ValidateConstant.ERROR_REQUIRED,
                    data.holdTimeMeasure ? null : ValidateConstant.ERROR_REQUIRED
                );
        return new FieldApplicantSummary(data, fieldErrors);
    };

export const getEmploymentInfoFieldSummary =
    (employmentInfoResponse: LoanApplicationTurboCashSummaryDTO)
        : FieldApplicantSummary<EmploymentInfoSummaryDTO, WorkDataTurboCashStepViewErrorFieldState> => {
        const isUndefined = employmentInfoResponse.employmentInfoDTO === undefined;
        const data = !isUndefined ? employmentInfoResponse.employmentInfoDTO : new EmploymentInfoSummaryDTO();
        const fieldErrors =
            isUndefined ?
                defaultState.turboCash.ui.workDataStepView.fieldErrors :
                new WorkDataTurboCashStepViewErrorFieldState(
                    data.workplaceName ? null : ValidateConstant.ERROR_REQUIRED,
                    data.position ? null : ValidateConstant.ERROR_REQUIRED,
                    data.workingSince.displayValue ? null : ValidateConstant.ERROR_REQUIRED,
                    data.address ? null : ValidateConstant.ERROR_REQUIRED,
                    data.extNumber ? null : ValidateConstant.ERROR_REQUIRED,
                    null,
                    data.streets ? null : ValidateConstant.ERROR_REQUIRED,
                    data.workplacePhone ? null : ValidateConstant.ERROR_REQUIRED,
                    null,
                    data.monthlyIncomes.displayValue ? null : ValidateConstant.ERROR_REQUIRED,
                    data.postalCode ? null : ValidateConstant.ERROR_REQUIRED,
                    !!data.state && data.state.value ? null : ValidateConstant.ERROR_REQUIRED,
                    null,
                    !!data.municipality && data.municipality.value ?
                        null : ValidateConstant.ERROR_REQUIRED,
                    !!data.colony && data.colony.value ? null : ValidateConstant.ERROR_REQUIRED,
                    !!data.employmentBussinessType && data.employmentBussinessType.value
                        ? null : ValidateConstant.ERROR_REQUIRED
                );
        return new FieldApplicantSummary(data, fieldErrors);
    };

export const getReferencesFieldSummary =
    (referencesResponse: LoanApplicationTurboCashSummaryDTO)
        : FieldApplicantSummary<[ReferencesSummaryDTO], {}> => {
        const isUndefined = referencesResponse.references === undefined;
        const data: [ReferencesSummaryDTO] =
            !isUndefined ? referencesResponse.references : [new ReferencesSummaryDTO()];
        return new FieldApplicantSummary(data, {});
    };

export const getDocumentGroupsFieldSummary =
    (documentGroupsResponse: LoanApplicationTurboCashSummaryDTO)
        : FieldApplicantSummary<[DocumentsSummaryDTO], {}> => {
        const isUndefined = documentGroupsResponse.documents === undefined;
        const data: [DocumentsSummaryDTO] =
            !isUndefined ? documentGroupsResponse.documents : [new DocumentsSummaryDTO()];
        return new FieldApplicantSummary(data, {});
    };

export const getCalculatorDataFieldSummary =
    (calculatorDataResponse: LoanApplicationApplicantSummaryDTO)
        : FieldApplicantSummary<CalculatorApplicantSummaryDTO, CalculatorStepViewFieldErrorState> => {
        const isUndefined = calculatorDataResponse.calculator === undefined;
        const data = !isUndefined ? calculatorDataResponse.calculator : new CalculatorApplicantSummaryDTO();
        const fieldErrors =
            isUndefined ?
                defaultState.creditoReal.ui.calculatorStepView.fieldErrors :
                new CalculatorStepViewFieldErrorState();
        return new FieldApplicantSummary(data, fieldErrors);
    };

export const getDesiredVehicleDataFieldSummary =
    (desiredVehicleDataResponse: LoanApplicationApplicantSummaryDTO)
        : FieldApplicantSummary<DesiredVehicleApplicantSummaryDTO, CalculatorStepViewFieldErrorState> => {
        const isUndefined = desiredVehicleDataResponse.desiredVehicle === undefined;
        const data = !isUndefined ? desiredVehicleDataResponse.desiredVehicle : new DesiredVehicleApplicantSummaryDTO();
        const fieldErrors =
            isUndefined ?
                defaultState.creditoReal.ui.calculatorStepView.fieldErrors :
                new CalculatorStepViewFieldErrorState();
        return new FieldApplicantSummary(data, fieldErrors);
    };

export const getHomeDataFieldSummary =
    (dataResponse: LoanApplicationApplicantSummaryDTO)
        : FieldApplicantSummary<HomeApplicantSummaryDTO, HomeDataStepViewFieldErrorState> => {
        const isUndefined = dataResponse.home === undefined;
        const data = !isUndefined ? dataResponse.home : new HomeApplicantSummaryDTO();
        const fieldErrors =
            isUndefined ?
                defaultState.creditoReal.ui.homeDataStepView.fieldErrors :
                new HomeDataStepViewFieldErrorState();
        return new FieldApplicantSummary(data, fieldErrors);
    };

export const getEmploymentDataFieldSummary =
    (employmentDataResponse: LoanApplicationApplicantSummaryDTO)
        : FieldApplicantSummary<WorkApplicantSummaryDTO, WorkDataStepViewErrorFieldState> => {
        const employmentData = employmentDataResponse.employmentData;
        const isUndefined = employmentData === undefined;
        const fieldErrors =
            isUndefined ?
                defaultState.creditoReal.ui.workDataStepView.fieldErrors : new WorkDataStepViewErrorFieldState(
                !!employmentData.workplaceName ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.position ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.workingSince ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.address ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.extNumber ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.intNumber ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.postalCode ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.state && employmentData.state.value ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.city && employmentData.city.value ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.municipality && employmentData.municipality.value ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.colony && employmentData.colony.value ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.workplacePhone ?
                    null : ValidateConstant.ERROR_REQUIRED,
                !!employmentData.monthlyIncomes && employmentData.monthlyIncomes.value > 0 ?
                    null : ValidateConstant.ERROR_REQUIRED,
                null,
                null,
                !!employmentData.employmentBusinessType && employmentData.employmentBusinessType.value
                    ? null
                    : employmentData.employmentRelationshipId === CR_DEPENDENT_CATALOG_ID
                        ? null
                        : ValidateConstant.ERROR_REQUIRED,
                !isEmpty(employmentData.invoiceInfo?.billingTypeId) ?
                    null : ValidateConstant.ERROR_REQUIRED,
                employmentData.invoiceInfo?.billingTypeId === 'TAX' && isEmpty(employmentData.invoiceInfo?.taxRegimeId)
                    ? ValidateConstant.ERROR_REQUIRED : null
                );
        const data = !isUndefined ? new WorkApplicantSummaryDTO(
            employmentData.workplaceName ? employmentData.workplaceName : GlobalConstants.EMPTY_STRING,
            employmentData.position ? employmentData.position : GlobalConstants.EMPTY_STRING,
            employmentData.workingSince ? employmentData.workingSince : GlobalConstants.EMPTY_STRING,
            employmentData.address ? employmentData.address : GlobalConstants.EMPTY_STRING,
            employmentData.extNumber ? employmentData.extNumber : GlobalConstants.EMPTY_STRING,
            employmentData.intNumber ? employmentData.intNumber : GlobalConstants.EMPTY_STRING,
            employmentData.postalCode ? employmentData.postalCode : GlobalConstants.EMPTY_STRING,
            employmentData.state ? employmentData.state : GlobalConstants.EMPTY_CATALOG,
            employmentData.city ? employmentData.city : GlobalConstants.EMPTY_CATALOG,
            employmentData.municipality ? employmentData.municipality : GlobalConstants.EMPTY_CATALOG,
            employmentData.colony ? employmentData.colony : GlobalConstants.EMPTY_CATALOG,
            employmentData.colonies ? employmentData.colonies : [],
            employmentData.workplacePhone ? employmentData.workplacePhone : GlobalConstants.EMPTY_STRING,
            employmentData.workplacePhoneExt ? employmentData.workplacePhoneExt : GlobalConstants.EMPTY_STRING,
            !!employmentData.monthlyIncomes && employmentData.monthlyIncomes.value > 0,
            employmentData.monthlyIncomes ?
                employmentData.monthlyIncomes : GlobalConstants.EMPTY_DISPLAY_VALUE,
            employmentData.employmentRelationshipId ?
                employmentData.employmentRelationshipId : GlobalConstants.EMPTY_STRING,
            employmentData.employmentBusinessType ?
                employmentData.employmentBusinessType : GlobalConstants.EMPTY_CATALOG,
            employmentData.incomeProof ?
                employmentData.incomeProof : GlobalConstants.EMPTY_CATALOG,
            employmentData.invoiceInfo,
        ) : new WorkApplicantSummaryDTO();
        return new FieldApplicantSummary(data, fieldErrors);
    };
